import { DeleteOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useIntegrations, useDeleteIntegration } from '_/hooks/useIntegrations';
import useOrgAccess from '_/hooks/useOrgAccess';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Button, Popconfirm, Space, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { ReactElement, useState, useEffect, useCallback } from 'react';

import NewIntegration from './NewIntegration';

interface Integration {
  id: string;
  name: string;
  createdOn: string;
  href: string;
}

function Integrations(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { data: integrations = [], refetch, isRefetching } = useIntegrations(selectedOrgSlug);
  const { mutate: deleteIntegration } = useDeleteIntegration(selectedOrgSlug || '');
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIntegrationId, setEditingIntegrationId] = useState<string | undefined>(undefined);

  // Get layout contexts
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  const handleNewAlarmIntegration = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingIntegrationId(undefined);
    setModalOpen(true);
  }, []);

  const handleRowClick = (record: Integration) => {
    if (access) {
      setEditingIntegrationId(record.id);
      setModalOpen(true);
    }
  };

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingIntegrationId(undefined);
    refetch();
  }, [refetch]);

  const handleDeleteIntegration = (integrationId: string) => {
    deleteIntegration(integrationId);
  };

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Integrations' },
    ]);
    setActionButtons([
      {
        onClick: handleNewAlarmIntegration,
        icon: <PlusCircleOutlined />,
        label: 'Add Integration',
        disabled: !access,
      },
    ]);
    setOnRefresh(refetch);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBreadcrumbs, setActionButtons, setOnRefresh, selectedOrgSlug, selectedOrganization, access, handleNewAlarmIntegration]);

  // Update refresh loading state
  useEffect(() => {
    setRefreshLoading(isRefetching);
  }, [isRefetching, setRefreshLoading]);

  // Filter integrations based on search text
  const filteredIntegrations = integrations.filter((integration) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return integration.name?.toLowerCase().includes(lowerSearch) || integration.id?.toLowerCase().includes(lowerSearch);
  });

  const columns: ColumnsType<Integration> = [
    {
      title: 'Type',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: Integration) => (
        <a
          onClick={() => handleRowClick(record)}
          style={{
            cursor: access ? 'pointer' : 'default',
            color: access ? undefined : 'rgba(0, 0, 0, 0.25)',
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: (a, b) => {
        // Parse the formatted date string back to compare
        const dateA = dayjs(a.createdOn, 'MMM DD, YYYY');
        const dateB = dayjs(b.createdOn, 'MMM DD, YYYY');
        return dateA.unix() - dateB.unix();
      },
      render: (date: string) => date,
    },
    {
      title: '',
      key: 'action',
      width: 150,
      render: (_: any, record: Integration) => (
        <Space>
          <Popconfirm
            title="Delete Integration"
            description="Are you sure you want to delete this integration?"
            onConfirm={() => handleDeleteIntegration(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={!access}
          >
            <Button type="text" icon={<DeleteOutlined />} danger disabled={!access} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Input
          placeholder="Search integrations..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Table
          columns={columns}
          dataSource={filteredIntegrations}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Space>
      <NewIntegration open={modalOpen} onClose={handleModalClose} integrationId={editingIntegrationId} />
    </>
  );
}

export default Integrations;
